<script>
import { Helper, debounce } from '@/mixin/helper'
import Multiselect from 'vue-multiselect'
import * as customProfileApi from '@/services/rest-api-sms/lib/custom-profile/custom-profile'
import CustomProfileModal from './customProfileModal'

export default {
  props: {
    companies: {
      type: Array,
      default: () => [],
    },
    deviceTypes: {
      type: Array,
      default: () => [],
    },
    sensors: {
      type: Array,
      default: () => [],
    },
    deviceCommands: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    Multiselect,
    CustomProfileModal,
  },
  mixins: [Helper],
  data() {
    return {
      isLoading: false,
      search: '',
      selectedDomainName: null,
      profiles: [],
      tableRows: [],
      pagination: {
        page: 1,
        limit: 10,
        totalCount: 0,
      },
      pageOptions: [10, 25, 50, 100],
    }
  },
  created() {
    this.getCustomProfiles()
  },
  computed: {
    fields() {
      return [
        {
          key: 'profile_name',
          label: 'Profile',
          sortable: true,
          isRedirectable: true,
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true,
        },
        {
          key: 'profile_code',
          label: 'Profile Code',
          sortable: true,
        },
        {
          key: 'company_id',
          label: 'Domain',
          sortable: true,
        },
        {
          key: 'device_name',
          label: 'Nama Perangkat',
          sortable: true,
        },
        {
          key: 'device_code',
          label: 'Kode Perangkat',
          sortable: true,
        },
        {
          key: 'sensor_name',
          label: 'Nama Sensor',
          sortable: true,
        },
        {
          key: 'sensor_code',
          label: 'Kode Sensor',
          sortable: true,
          thClass: 'custom-profile-command__sensor-code',
          tdClass: 'custom-profile-command__sensor-code',
        },
        {
          key: 'command_type',
          label: 'Tipe Command',
          sortable: true,
        },
        {
          key: 'command',
          label: 'Command',
          sortable: true,
        },
        {
          key: 'created_at',
          label: 'Dibuat pada',
          sortable: false,
        },
        {
          key: 'action',
          label: 'Aksi',
          sortable: false,
          thClass: 'custom-profile-command__action',
          tdClass: 'custom-profile-command__action',
        },
      ]
    },
    paginationStart() {
      return this.tableRows.length ? (this.pagination.page - 1) * this.pagination.limit + 1 : 0
    },
    paginationEnd() {
      return Math.min(
        this.pagination.totalCount,
        (this.pagination.page - 1) * this.pagination.limit + this.tableRows.length
      )
    },
  },
  methods: {
    async getCustomProfiles() {
      this.isLoading = true

      const response = await customProfileApi
        .get({
          page: this.pagination.page,
          limit: this.pagination.limit,
          search: this.search || undefined,
          filters: this.getCompanyFilters(),
        })
        .catch((error) => {
          this.handleError(error)
        })

      this.isLoading = false

      if (!response?.status) {
        this.profiles = []
        this.tableRows = []
        this.pagination.totalCount = 0
        return
      }

      this.profiles = response.data?.paginated_result || []
      this.tableRows = this.mapProfilesToRows(this.profiles)
      this.pagination.page = response.metadata?.page || this.pagination.page
      this.pagination.totalCount = response.metadata?.total_count || 0
    },
    mapProfilesToRows(profiles) {
      return profiles.flatMap((profile) => {
        const commandGroups = this.groupCommandsByMapping(profile.commands?.length ? profile.commands : [{}])
        let hasRenderedProfile = false

        return commandGroups.map((commands) => {
          const shouldRenderProfile = !hasRenderedProfile
          if (shouldRenderProfile) hasRenderedProfile = true

          return this.mapCommandGroupToRow({
            profile,
            commands,
            shouldRenderProfile,
          })
        })
      })
    },
    groupCommandsByMapping(commands) {
      const groups = []
      const groupIndexes = {}

      commands.forEach((command) => {
        const key = `${command.device_code || 'empty'}-${command.sensor_code || 'empty'}`
        if (groupIndexes[key] === undefined) {
          groupIndexes[key] = groups.length
          groups.push([])
        }

        groups[groupIndexes[key]].push(command)
      })

      return groups
    },
    mapCommandGroupToRow({ profile, commands, shouldRenderProfile }) {
      const firstCommand = commands[0] || {}
      const commandItems = commands
        .filter((command) => command.command)
        .map((command) => ({
          device_code: command.device_code,
          sensor_code: command.sensor_code,
          command_type: command.command_type || '',
          command: command.command?.trim?.() || command.command || '',
        }))

      const response = {
        id: `${profile.custom_profile_id}-${firstCommand.device_code || 'empty'}-${firstCommand.sensor_code || 'empty'}`,
        row_class: '',
        custom_profile_id: profile.custom_profile_id,
        profile_name: shouldRenderProfile ? profile.name : '',
        type: shouldRenderProfile ? this.formatType(profile.type) : '',
        profile_code: shouldRenderProfile ? profile.code : '',
        device_name: this.mapDeviceName(firstCommand.device_code),
        company_id: shouldRenderProfile ? this.mapCompanyName(profile.company_id) : '',
        device_code: firstCommand.device_code ?? 0,
        sensor_name: this.mapSensorName(firstCommand.sensor_code),
        sensor_code: Number(firstCommand.sensor_code),
        command_type: commandItems.map((command) => command.command_type).join(', '),
        command: commandItems.map((command) => command.command).join(', '),
        command_items: commandItems,
        created_at: this.momentIsoToDMYHms(profile.created_at),
      }
      return response
    },
    mapDeviceName(deviceCode) {
      if (!deviceCode) return ''
      const device = this.deviceTypes.find(
        (item) => Number(item.deviceCode ?? item.device_code) === Number(deviceCode)
      )
      return device?.deviceName || device?.device_name || '-'
    },
    mapCompanyName(companyId) {
      if (!companyId) return ''
      const company = this.companies.find(
        (item) => Number(item.id) === Number(companyId)
      )
      return company?.domainName || company?.name || '-'
    },
    mapSensorName(sensorCode) {
      if (sensorCode === 0) return 'Default Setting'
      const sensor = this.sensors.find((item) => Number(item.sensor_code) === Number(sensorCode))
      return sensor?.sensor_name ?? 'Default Setting'
    },
    formatType(type) {
      if (!type) return ''
      return type
        .toLowerCase()
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join('-')
    },
    getTypeVariant(type) {
      if (type === 'Primary') return 'badge-soft-success'
      return 'badge-soft-warning'
    },
    getCompanyFilters() {
      return this.selectedDomainName ? [`company_id eq ${this.selectedDomainName.id}`] : undefined
    },
    customLabel(item) {
      return `${item.name} (${item.domainName})`
    },
    handleCompanyChange() {
      this.pagination.page = 1
      this.getCustomProfiles()
    },
    handleSearchChange: debounce(function (search) {
      this.search = search
      this.pagination.page = 1
      this.getCustomProfiles()
    }, 500),
    handlePageChange(page) {
      this.pagination.page = page
      this.getCustomProfiles()
    },
    handleLimitChange(limit) {
      this.pagination.limit = limit
      this.pagination.page = 1
      this.getCustomProfiles()
    },
    showCreateModal() {
      this.$refs.customProfileModal.show('create')
    },
    showEditModal() {
      if (!this.profiles.length) {
        this.$toast.error('Profile belum tersedia untuk domain ini')
        return
      }

      this.$refs.customProfileModal.show('edit')
    },
    handleCustomProfileSaved() {
      this.pagination.page = 1
      this.getCustomProfiles()
    },
    async deleteCommand(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Yakin ingin menghapus custom profile`,
        {
          title: 'Konfirmasi',
          okTitle: 'Hapus',
          cancelTitle: 'Batal',
          okVariant: 'danger',
          centered: true,
        }
      )

      if (!confirmed) return

      try {
        this.isLoading = true
        await customProfileApi.deleteCommand({
          custom_profile_id: item.custom_profile_id,
          device_code: item.device_code,
          sensor_code: item.sensor_code ?? 0,
        })
        this.$toast.success('Data berhasil dihapus')
        await this.getCustomProfiles()
      } catch (error) {
        this.handleError(error)
        this.isLoading = false
      }
    },
    tableRowClass(item) {
      return item?.row_class || ''
    },
    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error || errorData.message)
        }
      } else if (error.request) {
        this.$toast.error(error.response?.statusText || error.message)
      } else {
        this.$toast.error(error.message)
      }
    },
  },
}
</script>

<template>
  <div class="custom-profile-command">
    <h4 class="card-title">Pilih Domain</h4>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <multiselect
                class="filter-company-select"
                v-model="selectedDomainName"
                :options="companies"
                placeholder="Domain"
                :custom-label="customLabel"
                style="max-width: 1000px"
                @input="handleCompanyChange"
              />
              <div class="custom-profile-command__actions">
                <b-input-group size="md" class="mc-has-search custom-profile-command__search">
                  <template #prepend>
                    <i class="ps-2 mc-icon icon-fill_search"></i>
                  </template>
                  <b-form-input
                    placeholder="Cari..."
                    type="search"
                    :value="search"
                    @input="handleSearchChange"
                    style="border-radius: 8px;"
                  ></b-form-input>
                </b-input-group>
                <b-button variant="primary" class="custom-profile-command__add" @click="showCreateModal">
                  <i class="bx bx-plus"></i>
                  Tambah
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="custom-profile-command__add"
                  :disabled="isLoading"
                  @click="showEditModal"
                >
                  <i class="bx bx-edit"></i>
                  Edit
                </b-button>
              </div>
            </div>

            <div class="table-responsive my-3 border border-1 mc-table-overflow">
              <b-table
                :items="tableRows"
                :fields="fields"
                :busy="isLoading"
                :tbody-tr-class="tableRowClass"
                fixed
                show-empty
                class="custom-profile-command__table editable-table"
                hover
              >
                <template #table-busy>
                  <div class="custom-profile-command__busy">
                    <b-spinner class="align-middle mb-2"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template>

                <template #empty>
                  <div class="text-center my-4">Tidak ada data</div>
                </template>

                <template #cell(profile_name)="data">
                  <span v-if="data.value" class="fw-bold text-primary">{{ data.value }}</span>
                </template>

                <template #cell(type)="data">
                  <b-badge v-if="data.value" :class="getTypeVariant(data.value)">
                    {{ data.value }}
                  </b-badge>
                </template>

                <template #cell(sensor_code)="data">
                  <b-badge variant="light">
                    {{ data.value }}
                  </b-badge>
                </template>

                <template #cell(command_type)="data">
                  <div class="custom-profile-command__stack">
                    <b-badge
                      v-for="(command, index) in data.item.command_items"
                      :key="`${data.item.id}-type-${index}`"
                      variant="primary"
                    >
                      {{ command.command_type }}
                    </b-badge>
                  </div>
                </template>

                <template #cell(command)="data">
                  <div class="custom-profile-command__stack">
                    <span
                      v-for="(command, index) in data.item.command_items"
                      :key="`${data.item.id}-command-${index}`"
                    >
                      {{ command.command }}
                    </span>
                  </div>
                </template>

                <template #cell(action)="data">
                  <div class="custom-profile-command__stack custom-profile-command__action-stack">
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click.stop="deleteCommand(data.item)"
                    >
                      <i class="bx bx-trash-alt"></i>
                    </b-button>
                  </div>
                </template>
              </b-table>
            </div>

            <div v-if="tableRows.length" class="row mt-3 custom-profile-command__pagination">
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_length">
                  <label class="d-inline-flex align-items-center custom-profile-command__pagination-info">
                    <span>Menampilkan</span>
                    <span class="mx-2">
                      <b-form-select
                        class="form-select form-select-sm"
                        :value="pagination.limit"
                        size="sm"
                        :options="pageOptions"
                        :disabled="isLoading"
                        @change="handleLimitChange"
                      />
                    </span>
                    <span>entri.&nbsp;&nbsp;</span>
                    <span>
                      Menampilkan&nbsp;
                      <strong>{{ paginationStart }}</strong>
                      &nbsp;sampai&nbsp;
                      <strong>{{ paginationEnd }}</strong>
                      &nbsp;dari&nbsp;
                      <strong>{{ pagination.totalCount }}</strong>
                      &nbsp;entri.
                    </span>
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-3">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <b-pagination
                    :disabled="isLoading"
                    :per-page="pagination.limit"
                    :total-rows="pagination.totalCount"
                    :value="pagination.page"
                    class="pagination-rounded mb-0"
                    @change="handlePageChange"
                  ></b-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CustomProfileModal
      ref="customProfileModal"
      :companies="companies"
      :deviceTypes="deviceTypes"
      :sensors="sensors"
      :profiles="profiles"
      :deviceCommands="deviceCommands"
      @saved="handleCustomProfileSaved"
    />
  </div>
</template>

